import { Link } from '@tanstack/react-router'
import { cohortLabel, useMyCohort, type PlatformRole } from '@lb/domain'
import { useTranslation } from '@lb/i18n'
import { IconChevronRight } from '@lb/ui'

import { http } from '../../lib/client'
import { formatDate } from '../../lib/format'
import { useSessionContext } from '../providers/session'

/**
 * The rail block that says what your standing is on THIS surface.
 *
 * Both prototypes carry one and the parent one does not: `pro-app` has a
 * **LICENCE** block naming the organisation whose seat you hold, `tsp-app` has
 * **YOUR PROGRAM** naming your cohort and where you are in it. We shipped
 * neither, so a practitioner on an employer's seat had nowhere in the product
 * that said so — and a TSP member had to open the cohort tab to learn which
 * month they were in.
 *
 * Both read data the shell already has or that one query answers, so this was
 * never blocked; it was simply never built.
 */
export function SurfacePanel({ role }: { role: PlatformRole }) {
  if (role === 'TSP') return <ProgramBlock />
  if (role === 'PRO' || role === 'ORG_ADMIN') return <LicenceBlock role={role} />
  return null
}

/**
 * `LICENCE` — how you hold your seat, in two shapes.
 *
 * ── It used to render for one of them ─────────────────────────────────────
 * We drew this only for a seat held through an ORGANISATION, reasoning that a
 * practitioner paying for their own subscription has no org admin to contact
 * and no seat somebody else renews, so a licence block would invent a
 * relationship. That was half right and the wrong half was load-bearing:
 * `pro-app.jsx:110` branches on the same fact and draws **both** sides —
 *
 * ```
 * ● CSS de Montréal                 ● Individual · Pro Annual
 * Seat managed by your …            Your subscription renews on March 3, 2027.
 * Contact your org admin ›          Manage it from your profile.
 * ```
 *
 * — so a self-paying practitioner simply had a shorter rail than the design,
 * and the one block on the surface that answers *"what am I on, and who
 * renews it"* was missing for exactly the people who renew it themselves.
 *
 * The individual side is not a licence-block-shaped consolation prize: it is
 * where the renewal date lives on every page, which is the thing a member
 * checks without wanting to open Billing.
 */
function LicenceBlock({ role }: { role: PlatformRole }) {
  const { t } = useTranslation()
  const { viewer } = useSessionContext()
  const access = viewer?.access

  /*
   * `NONE` — and anything before the entitlement read lands — draws nothing.
   * An unentitled practitioner has no seat to describe, and a block that says
   * so would be a paywall in the rail, which the design puts on the page.
   */
  if (!access || !access.entitled) return null

  const organisation = access.source === 'ORGANISATION'
  /*
   * The org ADMIN holds a seat in the same organisation they run, so `source`
   * alone put them on the seat-holder branch: *"Seat managed by your
   * organisation's admin — renewals and access are handled for you"*, over a
   * link reading *"Contact your org admin"*. Addressed to the person who IS
   * that admin, about work they do themselves.
   *
   * They get the same block naming the same organisation, and a sentence and a
   * link that are theirs — the seat list, which is the thing they came for.
   */
  const administers = role === 'ORG_ADMIN'

  return (
    <section className="rounded-[var(--r)] border border-border bg-canvas p-3.5">
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-slate">
        {t('surface.licence')}
      </p>
      <p className="mt-2 flex items-center gap-2 text-[13px] font-semibold text-ink">
        {/*
         * The design coloured this dot by seat type (`pro-lic-dot` vs `.solo`);
         * under the accent policy a dot is navy either way, and the sentence
         * beside it says whose seat it is.
         */}
        <span aria-hidden className="h-2 w-2 flex-none rounded-full bg-ink" />
        {organisation
          ? /* Named, not "your organisation" — the whole reason we asked the
               backend for `organisationName` beside `source`. */
            (access.organisationName ?? t('billing.yourOrganisation'))
          : t('surface.licenceIndividual', { plan: access.planName ?? '' })}
      </p>
      <p className="mt-1.5 text-[12px] leading-[1.45] text-muted">
        {organisation
          ? administers
            ? t('surface.licenceAdminBody')
            : t('surface.licenceBody')
          : /*
             * The renewal date, and only when we have one. A subscription
             * mid-cancellation has `currentPeriodEnd` too and this sentence
             * would call it a renewal — but the rail is not where that is
             * corrected, Billing is, and the design's block says nothing about
             * cancellation either.
             */
            access.currentPeriodEnd
            ? t('surface.licenceRenews', { date: formatDate(access.currentPeriodEnd) })
            : t('surface.licenceManage')}
      </p>
      {organisation ? (
        /*
         * "Contact your org admin ›" — it goes to Billing rather than opening
         * a message, because we hold no address for the administrator and
         * there is no in-product path to one. Billing is the screen that
         * already explains the seat relationship and says why there is no
         * cancel button; sending someone there is the honest version of the
         * design's button, where a control that opened nothing would not be.
         */
        <Link
          to={administers ? '/seats' : '/billing'}
          className="mt-2.5 inline-flex min-h-[24px] items-center gap-1 text-[12.5px] font-semibold text-ink hover:underline"
        >
          {administers ? t('surface.manageSeats') : t('surface.contactOrgAdmin')}
          <IconChevronRight size={12} aria-hidden />
        </Link>
      ) : null}
    </section>
  )
}

/**
 * `YOUR PROGRAM` — the cohort, and how far through it you are.
 *
 * Renders nothing when `useMyCohort` answers `null`, which is a TSP member not
 * currently enrolled. That is a real state — the design's block assumes a live
 * cohort — and an empty card saying "no programme" on the surface named after
 * the programme would be worse than the rail simply being shorter.
 */
function ProgramBlock() {
  const { t } = useTranslation()
  const cohort = useMyCohort(http)

  if (!cohort.data) return null

  const { cohort: programme, month } = cohort.data

  return (
    <section className="rounded-[var(--r)] border border-border bg-canvas p-3.5">
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-slate">
        {t('surface.program')}
      </p>
      <p className="mt-2 flex items-center gap-2 text-[13px] font-semibold text-ink">
        <span aria-hidden className="h-2 w-2 flex-none rounded-full bg-ink" />
        {t('surface.programName', { cohort: cohortLabel(programme) })}
      </p>
      <p className="mt-1.5 text-[12px] leading-[1.45] text-muted">
        {t('surface.programMonth', { month, total: programme.months })}
      </p>
      <Link
        to="/cohort"
        className="mt-2.5 inline-flex min-h-[24px] items-center text-[12.5px] font-semibold text-ink hover:underline"
      >
        {t('nav.cohortJourney')}
      </Link>
    </section>
  )
}
